import * as yup from "yup";
import {
  MIN_POLICY_CHARS,
  MAX_POLICY_CHARS,
  MAX_ZONE_NAME_CHARS,
  MAX_METHODS,
  ETA_RE,
  ETA_UNITS,
  MAX_ZONES
} from "./constants/validation";

import {
  COUNTRIES,
  METHOD_TYPES,
  DYNAMIC_BASES,
  COURIERS,
} from "./constants/index";
import { findCurrency } from "./utils/currencyList";
const etaUnits = ["business_days", "hours", "minutes"];

/* ── helpers ────────────────────────────────────────────────────── */
const noDup = (a) => new Set(a).size === a.length;
const coerceEmpty = (s) =>
  s.transform((val, orig) =>
    orig === "" || val === null || Number.isNaN(val) ? undefined : val
  );
const gt0 = () =>
  coerceEmpty(yup.number().typeError("Must be a number"))
    .moreThan(0, "Must be > 0");
const nonNeg = () =>
  coerceEmpty(yup.number().typeError("Must be a number"))
    .min(0, "Must be ≥ 0");/* ── postal-code object ────────────────────────────────────────── */
const postalObj = yup.object({
  code: yup.string().trim().required("Postal code is required"),
  include: yup.boolean().default(true),
});

/* ── rules (flat / dynamic / tiered) ───────────────────────────── */
const rulesSchema = yup.lazy((value, { parent }) => {
  const { type, useCourierApi } = parent;

  if (useCourierApi) {
    return yup.mixed().strip();            // ignored for live rates
  }

  if (type === "flat") {
   return yup.object({ rate: nonNeg().required("Rate required") });
  }

  if (type === "dynamic") {
    return yup.object({
     baseRate:  nonNeg().required("Base rate required"),
     ratePerKg: nonNeg().required("Rate per kg required"),
     minCharge: nonNeg().required("Minimum charge required"),
    });
  }

  if (type === "tiered") {
    return yup
      .array()
      .ensure()
      .of(
        yup.object({
         maxWeight: gt0().required("Max weight required"),
         rate: nonNeg().required("Rate required"),
        }),
      )
      .min(1, "Add at least one tier")
      .test(
        "unique-max-w",
        "Duplicate max-weight values",
        (rows) => {
          const vals = (rows || [])
            .map((r) => Number(r?.maxWeight))
            .filter((n) => Number.isFinite(n));
          return new Set(vals).size === vals.length;
        },
      );
  }

  /* fallback */
  return yup.mixed();
});
const intPos = () =>
  coerceEmpty(yup.number().typeError("Must be a number"))
    .integer("Must be a whole number")
    .min(1, "Must be ≥ 1");
const freeShippingSchema = yup.object({
  enabled: yup.boolean().default(false),
  conditions: yup.object({
    minSubtotal: nonNeg(),
    minItems: intPos(),
    maxWeight: gt0(),
  }).when("enabled", { is: false, then: (s) => s.strip() }),
  message: yup.string().when("enabled", {
    is: true,
    then: (s) => s.trim().min(3, "Message required").required("Message required"),
    otherwise: (s) => s.strip(),
  }),
})
.test("fs-at-least-one", "Add at least one free-shipping condition", function (fs) {
  if (!fs?.enabled) return true;
  const c = fs.conditions || {};
  return ["minSubtotal","minItems","maxWeight"].some((k) => c[k] != null);
});
/* ── method ────────────────────────────────────────────────────── */
const methodSchema = yup.object({
  name: yup.string().required("Method name is required"),
  type: yup.mixed().oneOf(METHOD_TYPES).required(),
  basis: yup.string().when("type", {
    is: "dynamic",
    then: (schema) =>
      schema.oneOf(DYNAMIC_BASES).required("Select weight or dimension"),
    otherwise: (schema) => schema.strip(),
  }),
  enabled: yup.boolean().default(true),

  etaUnit: yup.string().when("useCourierApi", {
    is: false,
    then: (s) => s.oneOf(etaUnits).required("Select a time unit"),
    otherwise: (s) => s.strip(),
  }),

  estimatedTime: yup.string().when("useCourierApi", {
    is: false,
    then: (s) =>
      s.required("ETA required")
       .test("eta-format", "Invalid ETA format", (v) => {
         if (!v) return false;
         return ETA_RE.test(v);
       }),
    otherwise: (s) => s.strip(),
  }),

  etaMin: yup
    .number()
    .transform((v, orig) => (orig === "" || orig == null ? NaN : v))
    .when("useCourierApi", {
      is: false,
      then: (s) =>
        s.typeError("Min value required")
         .integer("Min must be a whole number")
         .moreThan(0, "Min must be > 0")
         .required("Min value required"),
      otherwise: (s) => s.strip(),
    }),
  etaMax: yup
    .number()
    .transform((v, orig) => (orig === "" || orig == null ? NaN : v))
    .when("useCourierApi", {
      is: false,
      then: (s) =>
        s.typeError("Max value required")
         .integer("Max must be a whole number")
         .moreThan(0, "Max must be > 0")
         .required("Max value required")
         .test("gte-min", "Max must be ≥ Min", function (val) {
           const { etaMin } = this.parent;
           return typeof val === "number" && typeof etaMin === "number" ? val >= etaMin : false;
         }),
      otherwise: (s) => s.strip(),
    }),

  useCourierApi: yup.boolean().default(false),

  courier: yup.string().when("useCourierApi", {
    is: true,
    then: (s) => yup.string().oneOf(COURIERS).required("Courier required"),
    otherwise: (s) => s.strip(),
  }),

  rules: rulesSchema,
  freeShipping: freeShippingSchema,
});




/* ── zone ───────────────────────────────────────────────────────── */
const zoneSchema = yup.object({
  name: yup
    .string()
    .required("Zone name is required")
    .max(MAX_ZONE_NAME_CHARS, `Max ${MAX_ZONE_NAME_CHARS} chars`),

  countries: yup
    .array()
    // Accept any ISO-2 uppercase code so we aren't tied to the small demo list
    .of(yup.string().matches(/^[A-Z]{2}$/, "Invalid ISO country"))
    .min(1, "Select at least one country")
    .test("dupe-ctry", "Duplicate countries", noDup),

  postalRules: yup
    .object()
    .default({})
    .test("keys-are-countries", "Postal rules must be keyed by a selected country", function (obj) {
      if (!obj) return true;
      const countries = this.parent.countries || [];
      return Object.keys(obj).every((iso) => countries.includes(iso));
    })
    // each country: codes[].code required if row exists
    .test("code-required", "Postal code is required", (obj) => {
      if (!obj) return true;
      return Object.values(obj).every((r) =>
        !r || !Array.isArray(r.codes) || r.codes.every((c) => (c?.code || "").trim().length > 0)
      );
    })
    // no duplicates inside the same country bucket
    .test("dupe-codes-per-country", "Duplicate postal codes", (obj) => {
      if (!obj) return true;
      const norm = (s) => String(s || "").trim().toUpperCase();
      return Object.values(obj).every((r) => {
        if (!r || !Array.isArray(r.codes)) return true;
        const arr = r.codes.map((c) => norm(c.code));
        return new Set(arr).size === arr.length;
      });
    }),
  methods: yup
    .array()
    .of(methodSchema)
    .min(1, "Add at least one method")
    .max(MAX_METHODS, `Max ${MAX_METHODS} methods per zone`)
    .test("dupe-names", function (arr) {
      const items = (arr || []).map((m, idx) => ({
        idx,
        name: (m?.name || "").trim().toLowerCase(),
      }));
      const seen = new Map();
      for (const { idx, name } of items) {
        if (!name) continue; // blanks are handled by the required() rule
        if (seen.has(name)) {
          return this.createError({
            path: `${this.path}.${idx}.name`,
            message: "Duplicate method name",
          });
        }
        seen.set(name, idx);
      }
      return true;
    })
    .test(
      "dupe-eta",
      "Duplicate estimated-time values",
      (arr) => {
        const etas = arr
          .filter((m) => !m.useCourierApi && m.estimatedTime)
          .map((m) => m.estimatedTime.trim());
        return noDup(etas);
      },
    )
    .test("dupe-courier", function (arr) {
      const active = (arr || [])
        .map((m, idx) => ({ idx, on: !!m?.useCourierApi, courier: m?.courier }))
        .filter(x => x.on && x.courier); // ignore until a courier is selected
      const seen = new Map();
      for (const { idx, courier } of active) {
        if (seen.has(courier)) {
          return this.createError({
            path: `${this.path}.${idx}.courier`,
            message: "Only one live-rate method per courier",
          });
        }
        seen.set(courier, idx);
      }
      return true;
    })
});

/* ── root ───────────────────────────────────────────────────────── */
/* ── root ───────────────────────────────────────────────────────── */
export default yup.object({
  policies: yup.object({
    returnPolicy: yup
      .string()
      .required("Return policy is required")
      .min(MIN_POLICY_CHARS, `Min ${MIN_POLICY_CHARS} chars`)
      .max(MAX_POLICY_CHARS, `Max ${MAX_POLICY_CHARS} chars`),
    termsOfSale: yup
      .string()
      .required("Terms of sale is required")
      .min(MIN_POLICY_CHARS, `Min ${MIN_POLICY_CHARS} chars`)
      .max(MAX_POLICY_CHARS, `Max ${MAX_POLICY_CHARS} chars`),
  }),

  shippingGroups: yup
    .array()
    .of(zoneSchema)
    .max(MAX_ZONES, `Max ${MAX_ZONES} zones`)

    /* ⬇️ REPLACE this whole test with the block below */
    .test("zones-no-overlap", "Zones overlap on countries/postal codes", function (zones) {
      if (!Array.isArray(zones)) return true;
      const up = (s) => String(s || "").trim().toUpperCase();

      // Normalize a country rule for a given zone into coverage kind + set
      const norm = (rule) => {
        const codes = Array.isArray(rule?.codes) ? rule.codes : [];
        const set = new Set(codes.map((c) => up(c.code)));
        if (!rule || set.size === 0) return { kind: "whole", set }; // no codes ⇒ whole-country coverage
        return rule.mode === "exclude" ? { kind: "exclude", set } : { kind: "include", set };
      };

      // Decide overlap + which codes are conflicting (if we can list them)
      const overlaps = (A, B) => {
        // whole vs whole
        if (A.kind === "whole" && B.kind === "whole") return { yes: true, conflicts: null };

        // include vs whole (or vice versa)
        if (A.kind === "include" && B.kind === "whole") return { yes: A.set.size > 0, conflicts: new Set(A.set) };
        if (A.kind === "whole"   && B.kind === "include") return { yes: B.set.size > 0, conflicts: new Set(B.set) };

        // exclude vs whole (or vice versa) -> always overlaps
        if (A.kind === "exclude" && B.kind === "whole") return { yes: true, conflicts: null };
        if (A.kind === "whole"   && B.kind === "exclude") return { yes: true, conflicts: null };

        // include vs include -> overlap if intersection non-empty
        if (A.kind === "include" && B.kind === "include") {
          const inter = new Set([...A.set].filter((x) => B.set.has(x)));
          return { yes: inter.size > 0, conflicts: inter };
        }

        // include vs exclude -> no overlap only if include ⊆ excluded
        if (A.kind === "include" && B.kind === "exclude") {
          const bad = new Set([...A.set].filter((x) => !B.set.has(x))); // still covered by exclude-zone
          return { yes: bad.size > 0, conflicts: bad };
        }
        if (A.kind === "exclude" && B.kind === "include") {
          const bad = new Set([...B.set].filter((x) => !A.set.has(x)));
          return { yes: bad.size > 0, conflicts: bad };
        }

        // exclude vs exclude -> treat as overlap (can't prove disjoint without universe)
        if (A.kind === "exclude" && B.kind === "exclude") {
          return { yes: true, conflicts: null };
        }

        return { yes: false, conflicts: null };
      };

      // Pairwise compare zones on shared countries
      for (let i = 0; i < zones.length; i++) {
        for (let j = i + 1; j < zones.length; j++) {
          const Zi = zones[i] || {};
          const Zj = zones[j] || {};
          const ci = Zi.countries || [];
          const cj = Zj.countries || [];
          const common = ci.filter((c) => cj.includes(c));
          if (common.length === 0) continue;

          for (const iso of common) {
            const Ai = norm(Zi.postalRules?.[iso]);
            const Bj = norm(Zj.postalRules?.[iso]);
            const { yes, conflicts } = overlaps(Ai, Bj);
            if (!yes) continue;

            // Build a helpful message + place it where the user edited things
            const list = conflicts && conflicts.size > 0
              ? ` [${[...conflicts].slice(0, 6).join(", ")}${conflicts.size > 6 ? "…" : ""}]`
              : "";

            // Choose the most relevant path for the *second* zone (j)
            const pathFor = () => {
              // If j has include/exclude rules for this iso, point to them; else countries
              const rj = Zj.postalRules?.[iso];
              if (rj && Array.isArray(rj.codes)) return `shippingGroups.${j}.postalRules.${iso}`;
              return `shippingGroups.${j}.countries`;
            };

            // Tailored messages by case
            const msg = (() => {
              if (Ai.kind === "whole" && Bj.kind === "whole")
                return `${iso}: appears in multiple zones with no postal codes.`;
              if ((Ai.kind === "include" && Bj.kind === "whole") ||
                  (Ai.kind === "whole"   && Bj.kind === "include"))
                return `${iso}: country-level zone overlaps with specific included codes${list}.`;
              if ((Ai.kind === "exclude" && Bj.kind === "whole") ||
                  (Ai.kind === "whole"   && Bj.kind === "exclude"))
                return `${iso}: country-level zone overlaps with an 'exclude' zone.`;
              if (Ai.kind === "include" && Bj.kind === "include")
                return `${iso}: overlapping included postal codes${list}.`;
              if ((Ai.kind === "include" && Bj.kind === "exclude") ||
                  (Ai.kind === "exclude" && Bj.kind === "include"))
                return `${iso}: included codes must be fully excluded by the other zone${list}.`;
              if (Ai.kind === "exclude" && Bj.kind === "exclude")
                return `${iso}: two 'exclude' rules for the same country will overlap. Use 'include' rules to partition instead.`;
              return `${iso}: zones overlap.`;
            })();

            return this.createError({ path: pathFor(), message: msg });
          }
        }
      }
      return true;
    }),
  payments: yup.object({
   currency: yup
     .string()
     .test("iso4217", "Invalid currency", (v) => !!v && !!findCurrency(v))
     .required("Currency is required"),
    notificationEmail: yup.string().email().required(),
    contactEmail: yup.string().email().required(),
    contactPhone: yup.string().required(),
  }),
});


/* named re-exports (UI uses them) */
export {
  MIN_POLICY_CHARS,
  MAX_POLICY_CHARS,
  MAX_ZONE_NAME_CHARS,
  MAX_METHODS,
  ETA_RE,
};
